// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import xngGaPLPEvent from "@guestsupply/App/Handlers/GoogleAnalytics/PLP/PLPEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { XProductFilters } from "@guestsupply/XDL/types";
import { parseBoolean } from "@guestsupply/XDL/Utilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const productsDataView = getProductListDataView(state);
    const isAvendraPricingFilterOn = (state.pages.productList.productFilters as XProductFilters)?.isAvendraPricing;
    const isAvendraPropOn = parseBoolean((productsDataView as any)?.properties?.isAvendraPricing);

    return {
        loaded: !!productsDataView.value,
        displayFacetsForStockedItems: getSettingsCollection(state).productSettings.displayFacetsForStockedItems,
        isAven: getSession(state).properties?.exclusive?.toLowerCase() === "aven",
        isAuthenticated: xIsAuthenticated(state),
        isAvendraPricing: isAvendraPricingFilterOn || isAvendraPropOn,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
    // xng-change: add google event
    xngGaPLPEvent,
};

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}
interface OwnProps extends WidgetProps {}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListIsAvendraFilterStyles {}

export const productListIsAvendraFilterStyles: ProductListIsAvendraFilterStyles = {};

const styles = productListIsAvendraFilterStyles;

const ProductListIsAvendraFilter: FC<Props> = ({
    isAvendraPricing,
    loaded,
    addProductFilters,
    removeProductFilters,
    fields,
    isAven,
    isAuthenticated,
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaPLPEvent,
}) => {
    if (!loaded || !(isAven && isAuthenticated)) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ isAvendraPricing: false } as XProductFilters);
        } else {
            addProductFilters({ isAvendraPricing: true } as XProductFilters);
        }
        // xng-change: add google event
        xngGaPLPEvent({
            plpEventType: GOOGLE_EVENTS.PLP_FILTER,
            includePLPEvent: xGoogleAnalytics.includePLPFilterEvent,
            clickText: facet.name,
            attribute: translate("Avendra Pricing"),
        });
    };

    const facet = [
        {
            id: "Avendra",
            name: translate("Avendra Pricing"),
            count: -1,
            selected: isAvendraPricing ?? false,
        },
    ];

    return (
        <ProductListFiltersAccordionSection
            title={translate("Avendra Pricing")}
            facets={facet}
            onChangeFacet={onChangeFacet}
            showMoreLimit={1}
            extendedStyles={productListCategoryFiltersStyles}
            expandByDefault={fields.expandByDefault}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListIsAvendraFilter),
    definition: {
        group: "Product List",
        displayName: "Avendra Pricing Filter",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
